import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Star, HeartHandshake, ListMusic, Headphones } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../contexts/PlayerContext";

export default function CuratedSelections() {
    const [data, setData] = useState({ featured: [], team: [] });
    useEffect(() => {
        api.get("/tracks/selections").then(({ data }) => setData(data)).catch(() => {});
    }, []);

    if (!data.featured.length && !data.team.length) return null;

    return (
        <section className="border-t border-[#1c1c1c]" data-testid="curated-selections">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
                <div className={`grid grid-cols-1 ${data.featured.length && data.team.length ? "lg:grid-cols-2" : ""} gap-10`}>
                    {data.featured.length > 0 && (
                        <PlaylistBlock
                            tracks={data.featured}
                            kicker="Playlist"
                            title={<>À la <span className="text-[#ffc83d]">une</span></>}
                            icon={<Star className="w-4 h-4 text-[#ffc83d] fill-[#ffc83d]" />}
                            accent="#ffc83d"
                            testid="featured-playlist"
                        />
                    )}
                    {data.team.length > 0 && (
                        <PlaylistBlock
                            tracks={data.team}
                            kicker="Coup de cœur"
                            title={<>Sélection de l&apos;équipe <span className="text-[#00d97e]">MBOAZICK</span></>}
                            icon={<HeartHandshake className="w-4 h-4 text-[#00d97e]" />}
                            accent="#00d97e"
                            testid="team-playlist"
                        />
                    )}
                </div>
            </div>
        </section>
    );
}

function PlaylistBlock({ tracks, kicker, title, icon, accent, testid }) {
    const { current, playing, play, toggle } = usePlayer();

    const playAll = () => play(tracks[0], { queue: tracks });

    return (
        <div data-testid={testid}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-2 flex items-center gap-2" style={{ color: accent }}>
                {icon} {kicker}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
                <button
                    onClick={playAll}
                    className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full border transition-colors hover:opacity-80"
                    style={{ borderColor: `${accent}66`, color: accent }}
                    data-testid={`${testid}-play-all`}
                >
                    <ListMusic className="w-3.5 h-3.5" /> Tout écouter
                </button>
            </div>
            <div className="mt-5 space-y-2">
                {tracks.slice(0, 6).map((t, i) => {
                    const isCurrent = current?.id === t.id;
                    return (
                        <div
                            key={t.id}
                            className="group flex items-center gap-3 bg-[#101010] border border-[#262626] hover:border-[#3a3a3a] rounded-xl p-2.5 transition-colors"
                            data-testid={`${testid}-track-${t.id}`}
                        >
                            <button
                                onClick={() => (isCurrent ? toggle() : play(t, { queue: tracks }))}
                                className={`relative w-12 h-12 shrink-0 rounded-lg overflow-hidden ${isCurrent ? "ring-2" : ""}`}
                                style={isCurrent ? { "--tw-ring-color": accent } : {}}
                                data-testid={`${testid}-play-${t.id}`}
                                aria-label={isCurrent && playing ? "Pause" : `Écouter ${t.title}`}
                            >
                                {t.cover_url ? (
                                    <img decoding="async" src={t.cover_url} alt={t.title} loading="lazy" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="w-full h-full bg-[#1c1c1c] flex items-center justify-center text-lg">🎵</span>
                                )}
                                <span className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={isCurrent ? { opacity: 1 } : {}}>
                                    {isCurrent && playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
                                </span>
                            </button>
                            <div className="flex-1 min-w-0">
                                <Link to={`/track/${t.slug || t.id}`} className="block font-medium text-sm truncate hover:underline">{t.title}</Link>
                                <p className="text-xs text-[#A8A8A8] truncate">{t.artist_name}{t.genre ? ` • ${t.genre}` : ""}</p>
                            </div>
                            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-[#6E6E6E]">
                                <Headphones className="w-3 h-3" /> {t.plays >= 1000 ? `${(t.plays / 1000).toFixed(1)}k` : t.plays || 0}
                            </span>
                            <Link
                                to={`/track/${t.slug || t.id}`}
                                className="text-xs font-bold px-3.5 py-1.5 rounded-full transition-opacity hover:opacity-80"
                                style={{ background: accent, color: "#0F0F0F" }}
                                data-testid={`${testid}-buy-${t.id}`}
                            >
                                Acheter
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
